import { inject, Injectable } from '@angular/core';

import { EnvironmentService } from '@core/config/environment.service';

/**
 * Token persistence.
 *
 * Tokens are stored in `localStorage` (with an in-memory cache) so the session is shared across
 * tabs and survives a browser restart — you stay signed in until the token actually expires, and
 * opening the app in a new tab does not force a re-login.
 *
 * Access token: memory + `localStorage`.
 * Refresh token: `localStorage` when `refreshTokenTransport` is `body`.
 * When `refreshTokenTransport` is `httpOnlyCookie`, the refresh token is not readable from JS — the
 * browser sends the cookie automatically and we use silent refresh only.
 *
 * Note: `localStorage` is readable by same-origin scripts, so the app relies on its CSP/XSS posture
 * to protect the tokens; the access token is kept short-lived by the backend to limit exposure.
 */
@Injectable({ providedIn: 'root' })
export class TokenStorageService {
  private readonly env = inject(EnvironmentService);
  private accessToken: string | null = null;

  private readStore(key: string): string | null {
    if (typeof localStorage === 'undefined') {
      return null;
    }
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private writeStore(key: string, value: string): void {
    if (typeof localStorage === 'undefined') {
      return;
    }
    try {
      localStorage.setItem(key, value);
    } catch {
      // Private mode / quota — keep in-memory only for this tab.
    }
  }

  private removeStore(key: string): void {
    if (typeof localStorage === 'undefined') {
      return;
    }
    try {
      localStorage.removeItem(key);
    } catch {
      // noop
    }
  }

  getAccessToken(): string | null {
    if (this.accessToken) {
      return this.accessToken;
    }
    const stored = this.readStore(this.env.auth.accessTokenStorageKey);
    if (stored) {
      this.accessToken = stored;
    }
    return stored;
  }

  setAccessToken(token: string): void {
    this.accessToken = token;
    this.writeStore(this.env.auth.accessTokenStorageKey, token);
  }

  clearAccessToken(): void {
    this.accessToken = null;
    this.removeStore(this.env.auth.accessTokenStorageKey);
  }

  getRefreshToken(): string | null {
    if (this.env.auth.refreshTokenTransport === 'httpOnlyCookie') {
      return null;
    }
    return this.readStore(this.env.auth.refreshTokenStorageKey);
  }

  setRefreshToken(token: string): void {
    if (this.env.auth.refreshTokenTransport === 'httpOnlyCookie') {
      return;
    }
    this.writeStore(this.env.auth.refreshTokenStorageKey, token);
  }

  clearRefreshToken(): void {
    this.removeStore(this.env.auth.refreshTokenStorageKey);
  }

  clearAllTokens(): void {
    this.clearAccessToken();
    this.clearRefreshToken();
  }
}
